// Custom collection sort; see docs/custom-collection-sort.md for the agreed rules.
export const EVP_VENDORS = ['Evolution Powersports', 'Evolution Powersports LLC', 'EVP Shift-Tek', 'TAPP'];
export const NEW_PRODUCT_TAG = 'new_product';
// collectionReorderProducts accepts at most this many moves per call.
export const MAX_MOVES_PER_CALL = 250;

// Window for units sold; Shopify's own best-selling order only breaks ties within it.
export const SALES_WINDOW_DAYS = 365;

export const numericId = (id) => String(id).split('/').pop();

// `unitsSold` maps numeric product IDs to units sold in the window, or is null when sales data
// is unavailable; products missing from the map sold none.
export function productCriteria(product, salesRank, unitsSold = null) {
  return {
    // Untracked and continue-selling variants report availableForSale: true.
    inStock: (product.variants?.nodes || []).some(variant => variant.availableForSale),
    isNew: (product.tags || []).includes(NEW_PRODUCT_TAG),
    isEvp: EVP_VENDORS.includes(product.vendor),
    unitsSold: unitsSold ? unitsSold.get(numericId(product.id)) || 0 : null,
    // Never-published products have no date and sort after every published one.
    publishedAt: product.publishedAt ? Date.parse(product.publishedAt) : null,
    salesRank,
  };
}

// `products` must be in the collection's BEST_SELLING order; position becomes the sales rank.
// Newest published breaks ties in units sold. Without sales data, sales rank stands in for units
// sold and the date is skipped, since it would otherwise override the best-selling order. Sales
// rank is unique, so it also settles every remaining tie.
export function rankProducts(products, unitsSold = null) {
  return products
    .map((product, index) => ({product, criteria: productCriteria(product, index + 1, unitsSold)}))
    .sort(({criteria: a}, {criteria: b}) => Number(b.inStock) - Number(a.inStock)
      || Number(b.isNew) - Number(a.isNew)
      || Number(b.isEvp) - Number(a.isEvp)
      || (b.unitsSold ?? 0) - (a.unitsSold ?? 0)
      || (unitsSold ? (b.publishedAt ?? -Infinity) - (a.publishedAt ?? -Infinity) || 0 : 0)
      || a.salesRank - b.salesRank);
}

// Minimal moves for collectionReorderProducts. Moves apply sequentially, each removing the
// product and reinserting it at newPosition, so products on the longest run already in target
// order stay put and every other product is placed directly after its target predecessor.
export function planMoves(currentIds, targetIds) {
  const current = new Set(currentIds);
  if (current.size !== currentIds.length || new Set(targetIds).size !== targetIds.length
    || current.size !== targetIds.length || !targetIds.every(id => current.has(id))) {
    throw new Error('Collection products changed; reload before applying the sort.');
  }
  const targetIndex = new Map(targetIds.map((id, index) => [id, index]));
  const keep = longestIncreasingRun(currentIds.map(id => targetIndex.get(id)));
  const order = [...currentIds];
  const moves = [];
  targetIds.forEach((id, index) => {
    if (keep.has(index)) return;
    order.splice(order.indexOf(id), 1);
    const newPosition = index === 0 ? 0 : order.indexOf(targetIds[index - 1]) + 1;
    order.splice(newPosition, 0, id);
    moves.push({id, newPosition: String(newPosition)});
  });
  return moves;
}

// Target indexes forming the longest increasing subsequence of `values` (patience sorting).
function longestIncreasingRun(values) {
  const tails = [];
  const previous = new Array(values.length);
  values.forEach((value, i) => {
    let low = 0, high = tails.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (values[tails[mid]] < value) low = mid + 1; else high = mid;
    }
    previous[i] = low > 0 ? tails[low - 1] : -1;
    tails[low] = i;
  });
  const keep = new Set();
  for (let i = tails.length ? tails[tails.length - 1] : -1; i >= 0; i = previous[i]) keep.add(values[i]);
  return keep;
}
